import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { saveTenantMessage } from '@/lib/tenant-conversations-client';
import type { Message, WhatsAppErrorState } from '@/types/message-types';
import { createOptimisticMessage, cleanSingleMessage } from '@/lib/message-utils';
import { supabase } from '@/integrations/supabase/client';

// Debug logger — only outputs in development, prevents content/phone leaks in production
const debugLog = import.meta.env.DEV
  ? (...args: unknown[]) => console.log(...args)
  : (..._args: unknown[]) => {};

interface UseMessageSendingProps {
  conversationId: string;
  customerId: string;
  customerPhone?: string;
  onMessageSent?: (message?: Message) => void;
  onMessageAdded: (message: Message) => void;
  onMessageUpdated: (messageId: string, updates: Partial<Message>) => void;
  addRecentlySentMessage?: (messageId: string) => void;
  onLoadMessages?: () => void;
}

export function useMessageSending({
  conversationId,
  customerId,
  customerPhone,
  onMessageSent,
  onMessageAdded,
  onMessageUpdated,
  addRecentlySentMessage,
  onLoadMessages
}: UseMessageSendingProps) {
  const { user, tenantId } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('conversations');
  
  const [sending, setSending] = useState(false);
  const [whatsappError, setWhatsappError] = useState<WhatsAppErrorState | null>(null);
  const [requiresTemplate, setRequiresTemplate] = useState(false);
  const instanceIdRef = useRef(`msg-send-${Math.random().toString(36).substring(7)}`);

  const sendMessage = useCallback(async (
    content: string,
    messageType: string = 'text',
    selectedModelId?: string,
    aiSuggestionData?: Record<string, unknown>,
    mediaFiles?: unknown[],
    replyToWhatsappMessageId?: string
  ): Promise<void> => {
    if (!user || !content.trim()) return;
    
    setSending(true);
    setWhatsappError(null);
    setRequiresTemplate(false);

    debugLog(`[${instanceIdRef.current}] 📤 Sending message:`, { 
      content, 
      messageType, 
      conversationId,
      mediaFilesReceived: mediaFiles?.length || 0,
      fullMediaFiles: mediaFiles
    });

    // Debug logging for media files
    if (mediaFiles && mediaFiles.length > 0) {
      debugLog(`[${instanceIdRef.current}] 🎯 DEBUG: Media files analysis:`, {
        totalFiles: mediaFiles.length,
        files: mediaFiles.map((file, index) => ({
          index,
          type: file.type,
          status: file.status,
          mediaUrl: file.mediaUrl,
          fileName: file.file?.name,
          fileSize: file.file?.size,
          mimeType: file.file?.type,
          hasMediaUrl: !!file.mediaUrl,
          mediaUrlLength: file.mediaUrl?.length
        }))
      });
    }

    try {
      // Determine sender type based on AI suggestion data
      let senderType = 'human_agent'; // Default to human_agent for manual messages
      if (aiSuggestionData) {
        senderType = aiSuggestionData.senderType || 'ai_with_human_review_no_changes';
      }

      // Create optimistic message for immediate UI feedback
      const optimisticMessage = createOptimisticMessage(
        conversationId,
        user.id,
        content.trim(),
        messageType,
        selectedModelId,
        senderType,
        aiSuggestionData
      );
      
      // Track this message to prevent real-time duplicate processing
      addRecentlySentMessage?.(optimisticMessage.id);
      
      onMessageAdded(optimisticMessage);
      
      // Save to database with enhanced metadata
      const messageMetadata = aiSuggestionData ? {
        ...aiSuggestionData,
        originalModelId: selectedModelId
      } : null;

      // Extract media URL from mediaFiles if present
      const mediaUrl = mediaFiles && mediaFiles.length > 0 ? mediaFiles[0]?.mediaUrl : null;
      
      // Enhanced metadata for media messages
      let enhancedMetadata = messageMetadata;
      if (mediaFiles && mediaFiles.length > 0) {
        const mediaFile = mediaFiles[0];
        enhancedMetadata = {
          ...messageMetadata,
          original_filename: mediaFile.file?.name,
          file_size: mediaFile.file?.size,
          mime_type: mediaFile.file?.type,
          media_url: mediaFile.mediaUrl
        };
      }

      // Reply context — store in metadata.context.id so V2 renders quoted card symmetrically
      if (replyToWhatsappMessageId) {
        enhancedMetadata = { ...(enhancedMetadata ?? {}), context: { id: replyToWhatsappMessageId } };
      }

      debugLog(`[${instanceIdRef.current}] 💾 Saving message to DB:`, {
        hasMediaFiles: !!(mediaFiles && mediaFiles.length > 0),
        mediaUrl,
        messageType,
        enhancedMetadata
      });

      // Get user's formatted name for sender_id (blocking for data integrity)
      // ✅ FIX: Query public.users directly and await result before saving
      const sender_uuid = user.id; // Always use UUID for lookups
      let formattedSenderId = user.id; // Default fallback to UUID

      // Await formatted name BEFORE saving to ensure sender_id is correct
      try {
        const { data: userData } = await supabase
          .from('users')
          .select('auth_user_id, email, full_name, phone_number')
          .eq('auth_user_id', user.id)
          .single();

        if (userData?.full_name) {
          formattedSenderId = userData.full_name;
        } else if (userData?.email) {
          formattedSenderId = userData.email;
        }
        // else: formattedSenderId remains user.id (UUID fallback)
      } catch (error) {
        console.warn('[useMessageSending] ⚠️ Could not get formatted name, using UUID:', error);
        // formattedSenderId remains user.id (UUID fallback)
      }

      const { data: savedMessage, error: saveError } = await saveTenantMessage(
        conversationId,
        senderType,
        formattedSenderId,
        sender_uuid,
        content.trim(),
        messageType,
        null, // whatsappMessageId - will be filled later
        enhancedMetadata,
        mediaUrl // Pass the media URL to be saved
      );

      debugLog(`[${instanceIdRef.current}] 💾 Save message result:`, { savedMessage, saveError });
      debugLog(`[${instanceIdRef.current}] 🔍 DEBUG: savedMessage type:`, typeof savedMessage);
      debugLog(`[${instanceIdRef.current}] 🔍 DEBUG: savedMessage content:`, savedMessage);

      if (saveError) {
        console.error(`[${instanceIdRef.current}] ❌ Error saving message:`, saveError);
        onMessageUpdated(optimisticMessage.id, { status: 'failed' });
        toast({
          title: t('sendMessage.saveFailedTitle'),
          description: saveError.message,
          variant: "destructive",
        });
        return;
      }

      debugLog(`[${instanceIdRef.current}] ✅ Message saved to database:`, savedMessage?.id);

      // Update optimistic message with real data
      if (savedMessage) {
        const cleanMessage = cleanSingleMessage(savedMessage, instanceIdRef.current);

        // Track the real message ID as well to prevent real-time duplicate processing
        addRecentlySentMessage?.(cleanMessage.id);

        onMessageUpdated(optimisticMessage.id, {
          id: cleanMessage.id,
          created_at: cleanMessage.created_at,
          status: 'sent'
        });
      }

      // Send via WhatsApp if customer conversation
      debugLog(`[${instanceIdRef.current}] 🔍 Customer ID check: "${customerId}"`);
      if (customerId) {
        try {
          debugLog(`[${instanceIdRef.current}] 📱 Sending WhatsApp message via edge function...`);
          
          // Ensure we have the real database message ID, not the optimistic ID
          const realMessageId = savedMessage?.id;
          debugLog(`[${instanceIdRef.current}] 🔍 DEBUG: realMessageId type:`, typeof realMessageId);
          debugLog(`[${instanceIdRef.current}] 🔍 DEBUG: realMessageId content:`, realMessageId);
          if (!realMessageId) {
            throw new Error('Failed to get real message ID from database');
          }

          // Check if this is a media message
          const isMediaMessage = mediaFiles && mediaFiles.length > 0;
          const mediaFile = isMediaMessage ? mediaFiles[0] : null; // For now, handle one file at a time
          
          debugLog(`[${instanceIdRef.current}] 🔍 Media message analysis:`, {
            isMediaMessage,
            mediaFilesLength: mediaFiles?.length || 0,
            mediaFile: mediaFile ? {
              type: mediaFile.type,
              mediaUrl: mediaFile.mediaUrl,
              fileName: mediaFile.file?.name,
              hasFile: !!mediaFile.file,
              hasMediaUrl: !!mediaFile.mediaUrl,
              mediaUrlLength: mediaFile.mediaUrl?.length
            } : null,
            customerPhone,
            customerPhoneType: typeof customerPhone,
            customerPhoneLength: customerPhone?.length,
            conditionCheck: {
              isMediaMessage,
              hasMediaFile: !!mediaFile,
              hasMediaUrl: !!mediaFile?.mediaUrl,
              finalCondition: isMediaMessage && mediaFile?.mediaUrl
            }
          });

          if (isMediaMessage && mediaFile?.mediaUrl) {
            debugLog(`[${instanceIdRef.current}] 🎬 Sending media message:`, {
              messageType: mediaFile.type,
              mediaUrl: mediaFile.mediaUrl,
              fileName: mediaFile.file?.name,
              fileSize: mediaFile.file?.size,
              mimeType: mediaFile.file?.type,
              recipientPhone: customerPhone
            });

            if (!customerPhone) {
              console.error(`[${instanceIdRef.current}] ❌ No customer phone number available for media message`);
              toast({
                title: t('sendMessage.mediaFailedTitle'),
                description: t('sendMessage.phoneRequiredForMedia'),
                variant: "destructive",
              });
              onMessageUpdated(savedMessage?.id || optimisticMessage.id, { status: 'failed' });
              return;
            }

            const requestBody = {
              tenantId: tenantId,
              conversationId,
              customerId,
              messageId: realMessageId,
              messageType: mediaFile.type,
              mediaUrl: mediaFile.mediaUrl,
              fileName: mediaFile.file?.name,
              mimeType: mediaFile.file?.type,
              recipientPhone: customerPhone,
              senderType: 'agent',
              senderId: user.id,
              modelId: selectedModelId,
              replyToWhatsappMessageId
            };

            debugLog(`[${instanceIdRef.current}] 📤 Calling whatsapp-send-media with body:`, requestBody);
            debugLog(`[${instanceIdRef.current}] 🔍 DEBUG: About to pass messageId:`, realMessageId);
            debugLog(`[${instanceIdRef.current}] 🔍 DEBUG: messageId type check:`, typeof realMessageId);

            const { data: whatsappResult, error: whatsappError } = await supabase.functions.invoke('whatsapp-send-media', {
              body: requestBody
            });

            debugLog(`[${instanceIdRef.current}] 🎬 Media function response:`, { data: whatsappResult, error: whatsappError });

            if (whatsappError) {
              console.error(`[${instanceIdRef.current}] ❌ Media WhatsApp API error:`, whatsappError);
              handleWhatsAppError(whatsappError, savedMessage?.id || optimisticMessage.id);
              // Don't return — auto-resolve and user assignment must still run below
            }

            if (whatsappResult?.success && whatsappResult?.whatsappMessageId) {
              debugLog(`[${instanceIdRef.current}] ✅ Media WhatsApp message sent:`, whatsappResult.whatsappMessageId);
              
              // Update local state with WhatsApp message ID
              onMessageUpdated(savedMessage?.id || optimisticMessage.id, {
                external_id: whatsappResult.whatsappMessageId,
                status: 'sent'
              });
            } else if (whatsappResult?.error) {
              console.error(`[${instanceIdRef.current}] ❌ Media WhatsApp result error:`, whatsappResult.error);
              handleWhatsAppError(whatsappResult.error, savedMessage?.id || optimisticMessage.id);
            }
          } else {
            // Regular text message
            debugLog(`[${instanceIdRef.current}] 🚀 Calling edge function with:`, {
              conversationId,
              content: content.trim(),
              messageType,
              messageId: realMessageId
            });

            const { data: whatsappResult, error: whatsappError } = await supabase.functions.invoke('whatsapp-send-optimized', {
              body: {
                conversationId,
                content: content.trim(),
                messageType,
                messageId: realMessageId,
                recipientPhone: customerPhone,
                replyToWhatsappMessageId
              }
            });

            debugLog(`[${instanceIdRef.current}] 🔄 Edge function response:`, { data: whatsappResult, error: whatsappError });

            if (whatsappError) {
              console.error(`[${instanceIdRef.current}] ❌ WhatsApp API error:`, whatsappError);
              handleWhatsAppError(whatsappError, savedMessage?.id || optimisticMessage.id);
              // Don't return — auto-resolve and user assignment must still run below
            }

            if (whatsappResult?.success && whatsappResult?.external_id) {
              debugLog(`[${instanceIdRef.current}] ✅ WhatsApp message sent:`, whatsappResult.external_id);
              
              // 🎯 Update local state immediately - DB save handled by edge function
              onMessageUpdated(savedMessage?.id || optimisticMessage.id, {
                external_id: whatsappResult.external_id,
                status: 'sent'
              });

              debugLog(`[${instanceIdRef.current}] 💾 WhatsApp ID saved to DB by edge function`);
            } else if (whatsappResult?.error) {
              console.error(`[${instanceIdRef.current}] ❌ WhatsApp result error:`, whatsappResult.error);
              handleWhatsAppError(whatsappResult.error, savedMessage?.id || optimisticMessage.id);
            } else {
              console.warn(`[${instanceIdRef.current}] ⚠️ Unexpected response format:`, whatsappResult);
            }
          }
        } catch (whatsappErr) {
          console.error(`[${instanceIdRef.current}] ❌ WhatsApp sending failed:`, whatsappErr);
          setWhatsappError({
            type: 'api_error',
            message: t('sendMessage.whatsappGenericError')
          });
          
          // Mark the message as failed
          onMessageUpdated(savedMessage?.id || optimisticMessage.id, { status: 'failed' });
        }
      } else {
        debugLog(`[${instanceIdRef.current}] ⏭️ Skipping WhatsApp send - no customer ID`);
      }

      onMessageSent?.(savedMessage ? cleanSingleMessage(savedMessage, instanceIdRef.current) : optimisticMessage);

      // 👤 Assign user to conversation when they interact (manual OR AI assist modes)
      // This implements "Last Touch Wins" - whoever sends most recently becomes assigned
      // Fire-and-forget - runs AFTER WhatsApp send (optimization)
      const isHumanInteraction = senderType === 'human_agent' ||
                                 senderType.startsWith('ai_with_human_review');

      if (isHumanInteraction && user?.id) {
        // Fire-and-forget user assignment - runs after WhatsApp send
        supabase.rpc('get_user_tenant_schema').then(({ data: schema }) => {
          if (schema) {
            supabase.rpc('assign_user_to_conversation', {
              tenant_schema: schema,
              conversation_id: conversationId,
              user_id: user.id
            }).then(({ error: assignError }) => {
              if (assignError) {
                console.warn('Failed to assign user to conversation:', assignError);
                // Silent fail - non-critical feature
              } else {
                debugLog(`✅ User ${user.id} assigned to conversation ${conversationId}`);
              }
            }).catch(err => {
              console.warn('Error assigning user to conversation:', err);
              // Silent fail - shouldn't block message sending
            });
          }
        }).catch(err => {
          console.warn('Schema fetch error for user assignment:', err);
          // Silent fail - shouldn't block message sending
        });
      }

      // 🔄 Auto-resolve escalation when human agent responds (manual or AI assist modes)
      // Fire-and-forget - doesn't block UI or message sending
      if (isHumanInteraction && user?.id) {
        debugLog(`[${instanceIdRef.current}] 🔄 Starting auto-resolve in background for conversation ${conversationId}`);

        // Get schema and invoke Edge Function without awaiting
        supabase.rpc('get_user_tenant_schema').then(({ data: schema, error: schemaError }) => {
          if (schemaError || !schema) {
            console.warn(`[${instanceIdRef.current}] Failed to get schema for auto-resolve:`, schemaError);
            return;
          }

          // Call resolve-escalation Edge Function in background (fire-and-forget)
          supabase.functions.invoke('resolve-escalation', {
            body: {
              tenant_schema: schema,
              conversation_id: conversationId
            }
          }).then(({ data: resolveResult, error: resolveError }) => {
            if (resolveError || !resolveResult?.success) {
              console.warn(`[${instanceIdRef.current}] Auto-resolve failed (background):`, resolveError || resolveResult?.error);
            } else if (resolveResult?.was_already_resolved) {
              debugLog(`[${instanceIdRef.current}] ℹ️ No pending escalation to resolve (background)`);
            } else {
              debugLog(`[${instanceIdRef.current}] ✅ Escalation auto-resolved in background`);
            }
          }).catch(err => {
            console.warn(`[${instanceIdRef.current}] Auto-resolve error (background):`, err);
          });
        }).catch(err => {
          console.warn(`[${instanceIdRef.current}] Schema fetch error for auto-resolve:`, err);
        });
      }

      // IMMEDIATE: Trigger immediate message reload for instant visibility
      setTimeout(() => {
        debugLog(`[${instanceIdRef.current}] ⚡ Triggering immediate message reload after send`);
        onLoadMessages?.();
      }, 100); // Very short delay for immediate UI update
      
    } catch (error) {
      console.error(`[${instanceIdRef.current}] ❌ Send message error:`, error);
      toast({
        title: t('sendMessage.sendFailedTitle'),
        description: error instanceof Error ? error.message : t('sendMessage.unknownError'),
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  }, [user, conversationId, customerId, onMessageSent, onMessageAdded, onMessageUpdated, toast]);

  const handleWhatsAppError = useCallback((error: unknown, messageId: string) => {
    console.error(`[${instanceIdRef.current}] ❌ WhatsApp error:`, error);
    
    const errorMessage = error.message || t('sendMessage.whatsappGenericError');
    let toastTitle = t('sendMessage.whatsappSendFailedTitle');
    let toastDescription = errorMessage;

    if (error.code === 131049 || error.message?.includes('conversation window')) {
      setWhatsappError({
        type: 'closed_window',
        message: t('sendMessage.windowClosedMessage'),
        requiresTemplate: true
      });
      setRequiresTemplate(true);
      toastTitle = t('sendMessage.windowClosedTitle');
      toastDescription = t('sendMessage.windowClosedDescription');
    } else if (error.code === 4 || error.message?.includes('rate limit')) {
      setWhatsappError({
        type: 'rate_limit',
        message: t('sendMessage.rateLimitMessage')
      });
      toastTitle = t('sendMessage.rateLimitTitle');
      toastDescription = t('sendMessage.rateLimitDescription');
    } else if (error.message?.includes('(#10) Application does not have permission') || 
               error.message?.includes('OAuthException') ||
               (error.code === 400 && error.message?.includes('permission'))) {
      setWhatsappError({
        type: 'permission_error',
        message: t('sendMessage.permissionErrorMessage')
      });
      toastTitle = t('sendMessage.permissionErrorTitle');
      toastDescription = t('sendMessage.permissionErrorMessage');
    } else if (error.message?.includes('WhatsApp API configuration not found') || error.message?.includes('WhatsApp credentials not configured')) {
      setWhatsappError({
        type: 'configuration_error',
        message: t('sendMessage.configErrorMessage')
      });
      toastTitle = t('sendMessage.configErrorTitle');
      toastDescription = t('sendMessage.configErrorDescription');
    } else {
      setWhatsappError({
        type: 'api_error',
        message: errorMessage
      });
    }

    // Show toast notification for all errors
    toast({
      title: toastTitle,
      description: toastDescription,
      variant: "destructive",
    });
    
    onMessageUpdated(messageId, { status: 'failed' });
  }, [onMessageUpdated, toast]);

  const clearError = useCallback(() => {
    setWhatsappError(null);
    setRequiresTemplate(false);
  }, []);

  return {
    sending,
    whatsappError,
    requiresTemplate,
    sendMessage,
    clearError
  };
}